import { CONFIRMATION_TYPES } from '../../../enums/confirmation-types.enum';
import { LoadStatus } from '../../load-status';
import { NotificationIdState } from '../../utilities/notification-id.type';
import { NotificationStateUtilities } from '../../utilities/notification-state-utlities';
import { selectNotificationEntries } from '../../notification/selectors/notification.selector';
import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import {
    CONFIRMATION_COLLECTION_FEATURE_KEY,
    ConfirmationCollectionState,
    ConfirmationIdentityState,
    ConfirmationState,
} from '../confirmation-collection.state';
import { isConfirmationFor } from '../utility/confirmation-utilities';

export const selectConfirmationFeature = createFeatureSelector<ConfirmationCollectionState>(
    CONFIRMATION_COLLECTION_FEATURE_KEY,
);

export const selectConfirmations: MemoizedSelector<object, ConfirmationState[]> = createSelector(
    selectConfirmationFeature,
    (state) => state.confirmations,
);

/**
 * The open confirmations as the timesheet's read-time projection needs them: their identity and the
 * times recorded on them.
 *
 * Narrowed rather than handing over whole confirmations, so the projection states exactly what it
 * depends on — nothing about materials, expenses or the service report can change what the timesheet
 * derives.
 */
export const selectInProgressConfirmations = createSelector(selectConfirmations, (confirmations) =>
    confirmations.map((confirmation) => ({
        notificationId: confirmation.notificationId,
        confirmationType: confirmation.confirmationType,
        timeRecording: confirmation.timeRecording,
    })),
);

export const selectConfirmationCollectionStatus: MemoizedSelector<object, LoadStatus> = createSelector(
    selectConfirmationFeature,
    (state) => state.status,
);

export const selectConfirmationCollectionLoaded: MemoizedSelector<object, boolean> = createSelector(
    selectConfirmationCollectionStatus,
    (status) => status === LoadStatus.Loaded,
);

/** `true` once the slice has settled (loaded from storage or failed to). Used to guard re-hydration. */
export const selectConfirmationsSettled: MemoizedSelector<object, boolean> = createSelector(
    selectConfirmationCollectionStatus,
    (status) => status === LoadStatus.Loaded || status === LoadStatus.Error,
);

/**
 * Return the stored confirmation for a notification/type pair, or `undefined` when none exists yet.
 * Confirmations are addressed by that pair throughout the slice, so neither this selector's callers
 * nor the facade hold any identity-derivation logic.
 *
 * @param notificationId
 * @param type
 */
/**
 * The type each notification's stored confirmation was recorded as, keyed by notification.
 *
 * A whole-collection projection rather than a per-notification factory, so a caller with many rows to
 * resolve — the timesheet, whose every work row links to the confirmation that recorded it — reads it
 * once instead of building a selector per row and missing its memo every time.
 *
 * Answers "which confirmation recorded this", which is what a link to it needs. Deriving the type from
 * the notification's available statuses instead answers a different question — which type it *could*
 * be confirmed with now — and the two disagree once a technician has recorded against one type while
 * the other was also on offer.
 */
export const selectConfirmationTypeByNotification: MemoizedSelector<
    object,
    ReadonlyMap<string, CONFIRMATION_TYPES>
> = createSelector(
    selectConfirmations,
    (confirmations) =>
        new Map(
            confirmations.map((confirmation) => [
                NotificationStateUtilities.notificationIdToString(confirmation.notificationId),
                confirmation.confirmationType,
            ]),
        ),
);

export const selectConfirmationFor = (
    notificationId: NotificationIdState,
    type: CONFIRMATION_TYPES,
): MemoizedSelector<object, ConfirmationState | undefined> =>
    createSelector(selectConfirmations, (confirmations) =>
        confirmations.find((confirmation) => isConfirmationFor(confirmation, notificationId, type)),
    );

/**
 * Returns `true` when a confirmation for the notification/type pair is already stored.
 *
 * @param notificationId
 * @param type
 */
export const selectConfirmationExists = (
    notificationId: NotificationIdState,
    type: CONFIRMATION_TYPES,
): MemoizedSelector<object, boolean> =>
    createSelector(selectConfirmationFor(notificationId, type), (confirmation) => confirmation !== undefined);

/**
 * The identities of the confirmations whose notification is gone, ready for `purgeStaleConfirmations`.
 *
 * Staleness is a query across two slices — the confirmations stored here and the notifications the
 * device currently has — so it is answered here rather than by an effect asking a service. Retained
 * notifications are deliberately not counted as present: `selectNotificationEntries` leaves them out,
 * and "gone" has to mean gone from the device's current list.
 *
 * An empty notification list yields nothing rather than everything. That is the conservatism
 * `StorageConfirmationService.purgeInvalidConfirmations` had: a degraded or partial load must never
 * look like "every notification is gone" and take a technician's work with it. It is also exactly the
 * guard this replaced — `isInitialized && entries.length > 0` collapses to `entries.length > 0`, since
 * `selectNotificationInitialized` is already satisfied by a non-empty list.
 *
 * @returns the identities to purge, empty when every confirmation still has its notification
 */
export const selectStaleConfirmations: MemoizedSelector<object, ConfirmationIdentityState[]> = createSelector(
    selectConfirmations,
    selectNotificationEntries,
    (confirmations, notifications) =>
        notifications.length === 0
            ? []
            : confirmations
                  .filter(
                      (confirmation) =>
                          !notifications.some((entry) =>
                              NotificationStateUtilities.compare(entry.notificationId, confirmation.notificationId),
                          ),
                  )
                  .map((confirmation) => ({
                      notificationId: confirmation.notificationId,
                      confirmationType: confirmation.confirmationType,
                  })),
);
